import { useQuery, useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/core';
import { READ_EXTERNAL_PAYMENT_METHODS } from 'graphql/queries/readExternalPaymentMethods';
import { useEffect, useMemo } from 'react';
import { parkingPaymentCardId } from 'states/common';
import { featureCountryCode } from 'states/persistInStorage';
import { usePermitStore } from 'stores/permitStore';
import { useUiStore } from 'stores/uiStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePaymentStore } from 'stores/paymentStore';
import {
  CountryCode,
  ExternalPaymentMethod,
  Query,
  QueryReadExternalPaymentMethodsArgs,
} from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { emptyQuery } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import { blockPaymentUsage } from 'utils/paymentUtils';

const IN_USE_PAYMENT_METHODS: [ExternalPaymentMethod] = ['SWISH'];

export type ExternalPaymentWrapper = {
  name: string;
  externalPaymentType: ExternalPaymentMethod;
};

type ExternalPaymentMethods = {
  externalPaymentMethods: ExternalPaymentWrapper[];
  isUseAllowed: boolean;
  externalPaymentMethod: ExternalPaymentWrapper | undefined;
  handleExternalPaymentComplete: () => void;
};

const wrap = (externalPaymentMethod: ExternalPaymentMethod) => {
  return {
    name: `${externalPaymentMethod?.charAt(0)}${externalPaymentMethod
      ?.slice(1)
      .toLowerCase()}`,
    externalPaymentType: externalPaymentMethod,
  } as ExternalPaymentWrapper;
};

const useExternalPaymentMethods = (): ExternalPaymentMethods => {
  const countryCode = useReactiveVar(featureCountryCode);
  const zoneCountry = useParkzoneStore((state) => state.parkZoneCountry);
  const selectedPaymentId = useReactiveVar(parkingPaymentCardId);
  const { data: { readExternalPaymentMethods } = {} } =
    useQuery<Query, QueryReadExternalPaymentMethodsArgs>(
      READ_EXTERNAL_PAYMENT_METHODS,
      {
        variables: { countryCode: countryCode as CountryCode },
        skip: !countryCode,
        fetchPolicy: 'cache-and-network',
      }
    ) ?? emptyQuery;
  const { popTo: navigatePark } = useNavigation<ParkNavProps>();
  const selectedTab = useParkzoneStore((state) => state.selectedParkingZoneTab);
  const blockedRoute = usePermitStore(
    (state) => state.blockCompanybenefitInEdit
  );
  const blockExternal = blockPaymentUsage(selectedTab, blockedRoute, true);

  const { resetParkingState, setCurrentPaymentMethodId } = useParkingStore(
    (store) => store
  );
  const restrictSwish = (method: ExternalPaymentMethod) => {
    return method === 'SWISH' && zoneCountry !== 'SE';
  };

  const externalPaymentMethods = useMemo(
    () =>
      (readExternalPaymentMethods?.paymentMethods ?? [])
        ?.filter(
          (methods) =>
            IN_USE_PAYMENT_METHODS.includes(methods) &&
            !isWeb &&
            !blockExternal &&
            !restrictSwish(methods)
        )
        .map(wrap),
    [readExternalPaymentMethods?.paymentMethods, restrictSwish]
  );

  const externalPaymentMethod = externalPaymentMethods.find(
    (method) => method.externalPaymentType === selectedPaymentId
  );

  const handleExternalPaymentComplete = () => {
    useParkingStore.getState().setResumeSessionPossible(false);
    resetParkingState();
    usePaymentStore.getState().setExternalPaymentFlowCompleted(false);
    usePaymentStore.getState().setExternalPaymentFlowLoading(false);
    usePaymentStore.getState().setExternalPaymentSessionId(null);
    useUiStore.getState().setExpandOngoingSheet(true);
    navigatePark('park');
  };

  useEffect(() => {
    if (blockExternal && externalPaymentMethod) {
      setCurrentPaymentMethodId(undefined);
    }
  }, [blockExternal, externalPaymentMethod, setCurrentPaymentMethodId]);

  return {
    externalPaymentMethods,
    isUseAllowed: blockExternal,
    externalPaymentMethod,
    handleExternalPaymentComplete,
  };
};

export default useExternalPaymentMethods;
